/**
 * useAudioLevels — real-time microphone amplitude via the Web Audio API.
 *
 * Connects a `MediaStream` (from `getUserMedia`) to an `AnalyserNode` and
 * samples frequency-bin amplitudes on each animation frame, returning an
 * array of normalised bar heights (0–1).  When the stream is `null` (not
 * recording) or the Web Audio API is unavailable, returns an all-zeros
 * array so the waveform renders flat without crashing.
 *
 * @param stream  The live `MediaStream` while recording, or `null`.
 * @param bars    Number of bars to render (default 5).
 * @returns       Float32-like array of `bars` values, each 0–1.
 */

import { useEffect, useRef, useState } from "react";

export function useAudioLevels(
  stream: MediaStream | null,
  bars = 5,
): number[] {
  const [levels, setLevels] = useState<number[]>(() => new Array(bars).fill(0));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!stream || typeof AudioContext === "undefined") {
      setLevels(new Array(bars).fill(0));
      return;
    }

    // Some browsers prefix AudioContext.
    const Ctor =
      AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) {
      setLevels(new Array(bars).fill(0));
      return;
    }

    const audioCtx = new Ctor();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    // fftSize determines frequency resolution.  32 is the minimum and
    // gives us plenty of bins for a handful of visual bars — we only need
    // a rough amplitude profile, not a detailed spectrum.
    analyser.fftSize = 32;
    source.connect(analyser);

    const binCount = analyser.frequencyBinCount; // fftSize / 2
    const freqData = new Uint8Array(binCount);

    const tick = () => {
      analyser.getByteFrequencyData(freqData);

      // Down-sample the frequency bins into `bars` buckets by averaging.
      // Voice energy is concentrated in lower bins, so we skip the top
      // quarter of the spectrum (mostly silence / noise floor).
      const usable = Math.max(1, Math.floor(binCount * 0.75));
      const per = Math.max(1, Math.floor(usable / bars));
      const next: number[] = new Array(bars);
      for (let i = 0; i < bars; i++) {
        let sum = 0;
        const start = i * per;
        const end = Math.min(start + per, usable);
        for (let j = start; j < end; j++) {
          sum += freqData[j];
        }
        // Normalise to 0–1 (byte data is 0–255).  Apply a slight gain
        // so quiet speech is still visible; clamp at 1.
        const avg = end > start ? sum / (end - start) : 0;
        next[i] = Math.min(1, (avg / 255) * 1.6);
      }
      setLevels(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      source.disconnect();
      analyser.disconnect();
      audioCtx.close().catch(() => {});
      setLevels(new Array(bars).fill(0));
    };
  }, [stream, bars]);

  return levels;
}
